import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Loader2, ArrowUpDown, AlertTriangle, Sparkles, GripVertical, Undo2, Plus, Package, Check, ChefHat, ShoppingBag, CupSoda, ArrowRight, Receipt, Tag, Printer, Fuel, ShoppingCart, X, Minus } from 'lucide-react';
import {
    DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext, arrayMove, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MapaCanvas from './MapaCanvas';
import UctenkaModal from './UctenkaModal';
import MistaSheet from './MistaSheet';
import useFullRoute from './hooks/useFullRoute';
import useVTeple from './hooks/useVTeple';
import { AKCENT, OK, CHYBA, POZOR, hm, kc, plocha, spodniList, Tlacitko, Stitek, Nadpis, Zpet, tint, stitekPlatby, ocekavanaPlatba, sestavTrasu, cinnostMista } from './ui';

/**
 * Skládání trasy — jedna obrazovka od tepla po výjezd (zjednodušeno 2026-10-12).
 *
 *   - mapa nahoře: trasa (fialová čísla) a objednávky v teple jako body
 *     `+číslo` — ťuknutím se přidají,
 *   - pod ní „V trase" (pořadí jde přetáhnout, zastávku vrátit do tepla)
 *     a „V teple" — ťuk na kartu = přidat do trasy; server pořadí sám srovná
 *     na nejrychlejší (RozvozService::seraditNaOptimum),
 *   - „Zkontrolovat a naložit" → kontrola u regálu po objednávkách (jedno
 *     „Mám" na objednávku, štítky jen ke kontrole) → „Naloženo" uloží
 *     kontrolu a cockpit přejde na V autě (Vyjezd.jsx): jízda, SMS a
 *     navigace začnou až Vyjíždím (2026-10-29; mezi 22. a 29. 10.
 *     spouštělo jízdu rovnou Naloženo),
 *   - objednávky se vybírají už v Mojí směně (K odvozu); V teple tu zůstává
 *     schované pod „přidat další z tepla",
 *   - dávku předanou kuchyní lze předem „Přijmout" (vím o ní, jedu zpět).
 *
 * Benzinky a obchody (2026-10-29): „+ Zastávka" přidá místo z okolí,
 * v trase je čárkovaná karta, jde přetáhnout jako objednávka a čas cesty
 * i pobytu je v odhadech všech zastávek za ní.
 *
 * Funguje i bez dávky: první přidaná objednávka ji založí. Dřív to byly
 * tři obrazovky (V teple → Příprava trasy → panel Přidat z tepla).
 */
export default function PripravaDavky({ data, onRefresh, onTiche, onZpet, isDark }) {
    const davka = data?.davka ?? null;
    const initialStops = davka ? (data.zastavky ?? []) : [];

    const [localStops, setLocalStops] = useState(initialStops);
    const initialMista = davka ? (data.mista ?? []) : [];
    const [localMista, setLocalMista] = useState(initialMista);
    const [mistaOtevrena, setMistaOtevrena] = useState(false);
    const [diff, setDiff] = useState(null);
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState(null);
    const [mapConfig, setMapConfig] = useState(null);
    // Kroky po trase: null → 'kontrola' (u regálu, Naloženo = jízda)
    const [krok, setKrok] = useState(null);
    const [pridatDalsi, setPridatDalsi] = useState(false);
    const lastReorderRef = useRef(null);
    const teplo = useVTeple();

    useEffect(() => {
        fetch('/api/rozvoz/config', { credentials: 'same-origin' })
            .then(r => r.ok ? r.json() : null).then(setMapConfig).catch(() => setMapConfig(null));
    }, []);

    // Sync od serveru jen když se objednávky reálně změnily (přibyla, ubyla,
    // jiné pořadí) — jinak by refetch přepsal rozdělané přetahování.
    const podpis = initialStops.map(s => s.objednavka_id).join(',');
    useEffect(() => { setLocalStops(initialStops); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [podpis]);
    // Zastávky: i změna doby a odhadu (po přepočtu na serveru).
    const podpisMist = initialMista.map(m => `${m.id}:${m.za_objednavkou_id}:${m.pobyt_min}:${m.cas_predpoklad}`).join(',');
    useEffect(() => { setLocalMista(initialMista); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [podpisMist]);

    const trasa = useMemo(() => sestavTrasu(localStops, localMista), [localStops, localMista]);
    const orderSignature = trasa.map(i => (i.typ === 'm' ? `${i.klic}@${i.m.za_objednavkou_id}` : i.klic)).join(',');
    const fullRoute = useFullRoute({ davkaId: davka?.id, orderSignature });

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const reorderToBackend = useCallback(async (orderedIds) => {
        if (!davka) return;
        setBusy('preradit');
        setErr(null);
        try {
            const r = await fetch(`/api/rozvoz/davky/${davka.id}/preradit`, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ poradi: orderedIds }),
            });
            if (!r.ok) throw new Error(`Pořadí se neuložilo (HTTP ${r.status})`);
            const json = await r.json();
            setDiff(json.diff ?? null);
            const nove = json.davka?.zastavky ?? [];
            if (nove.length) setLocalStops(nove);
            if (json.davka?.mista) setLocalMista(json.davka.mista);
        } catch (e) {
            setErr(e?.message || String(e));
        } finally {
            setBusy(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [davka?.id]);

    /** Nové pořadí celé trasy (objednávky i zastávky) → lokálně hned, server po chvilce. */
    const pouzitPoradi = useCallback((polozky) => {
        let za = null;
        const mista = [];
        polozky.forEach(i => {
            if (i.typ === 'o') za = i.z.objednavka_id;
            else mista.push({ ...i.m, za_objednavkou_id: za });
        });
        setLocalStops(polozky.filter(i => i.typ === 'o').map(i => i.z));
        setLocalMista(mista);
        clearTimeout(lastReorderRef.current);
        lastReorderRef.current = setTimeout(() => reorderToBackend(polozky.map(i => i.klic)), 200);
    }, [reorderToBackend]);

    const handleDragEnd = useCallback(({ active, over }) => {
        if (!over || active.id === over.id) return;
        const a = trasa.findIndex(i => i.klic === active.id);
        const b = trasa.findIndex(i => i.klic === over.id);
        if (a === -1 || b === -1) return;
        pouzitPoradi(arrayMove(trasa, a, b));
    }, [trasa, pouzitPoradi]);

    const handleVratitOptimum = useCallback(() => {
        const ids = diff?.optimum?.poradi_ids ?? data?.optimum?.poradi_ids;
        if (!Array.isArray(ids) || ids.length === 0) return;
        const byKlic = Object.fromEntries(trasa.map(i => [String(i.klic), i]));
        const novy = ids.map(id => byKlic[String(id)]).filter(Boolean);
        // Zastávky, které optimum nezná (přidané po výpočtu), zůstanou na konci.
        const zbytek = trasa.filter(i => !novy.includes(i));
        if (novy.filter(i => i.typ === 'o').length === localStops.length) pouzitPoradi([...novy, ...zbytek]);
    }, [diff, data?.optimum, trasa, localStops.length, pouzitPoradi]);

    const post = async (endpoint, { klic = endpoint, body = null, tiche = false } = {}) => {
        setBusy(klic);
        setErr(null);
        try {
            const r = await fetch(endpoint, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: body ? JSON.stringify(body) : null,
            });
            const json = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
            // Tiché obnovení nepřekreslí obrazovku spinnerem — mapa zůstane
            await (tiche ? (onTiche ?? onRefresh)() : onRefresh());
            return json;
        } catch (e) {
            setErr(e?.message || String(e));
            return null;
        } finally {
            setBusy(null);
        }
    };

    /** Ťuk na objednávku v teple (karta i bod v mapě) = do trasy. */
    const pridat = async (id) => {
        if (busy) return;
        setDiff(null);
        await post('/api/rozvoz/vzit', { klic: `pridat-${id}`, body: { objednavky_ids: [id] }, tiche: true });
        teplo.nacti();
    };
    const vratit = async (id) => {
        await post(`/api/rozvoz/zastavky/${id}/vratit`, { klic: `vratit-${id}`, tiche: true });
        teplo.nacti();
    };

    const stav = davka?.stav;
    const nalozitUrl = davka ? `/api/rozvoz/davky/${davka.id}/nalozit` : null;
    const bistro = data?.bistro?.lat ? data.bistro : mapConfig?.bistro;
    const hotove = teplo.data?.hotove ?? [];
    const variSe = teplo.data?.vari_se ?? [];
    const naSmene = teplo.data ? teplo.data.na_smene : true;
    // Stejné pole mezi rendery, jinak by mapa při každém překreslení
    // přestavěla body a znovu zazoomovala.
    const kandidatiPodpis = hotove.map(o => `${o.id}:${o.lat}:${o.lng}`).join('|');
    const kandidati = useMemo(
        () => (naSmene ? hotove.map(o => ({ id: o.id, lat: o.lat, lng: o.lng, cislo: o.cislo })) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [kandidatiPodpis, naSmene],
    );
    // Mapa vždy, aspoň s bistrem — prázdná horní polovina působila jako chyba
    const maMapu = !!bistro?.lat;
    const fitPadding = useMemo(() => ({ top: 72, bottom: 48, left: 32, right: 32 }), []);
    const pozde = localStops.filter(z => (z.rozdil_sec ?? 0) > 0).length;

    /** Doba na místě / odebrat zastávku — server přepočítá odhady celé trasy. */
    const upravitMisto = async (m, pobytMin) => {
        setLocalMista(prev => prev.map(x => (x.id === m.id ? { ...x, pobyt_min: pobytMin } : x)));
        await post(`/api/rozvoz/davky/${davka.id}/mista/${m.id}`, { klic: `misto-${m.id}`, body: { pobyt_min: pobytMin }, tiche: true });
    };
    const odebratMisto = async (m) => {
        setLocalMista(prev => prev.filter(x => x.id !== m.id));
        setBusy(`misto-${m.id}`);
        try {
            await fetch(`/api/rozvoz/davky/${davka.id}/mista/${m.id}`, { method: 'DELETE', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            await (onTiche ?? onRefresh)();
        } finally {
            setBusy(null);
        }
    };

    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--surface-base)', position: 'relative' }}>
            <div style={{ height: '40%', minHeight: 220, position: 'relative', flexShrink: 0 }}>
                {maMapu && (
                    <div style={{ position: 'absolute', inset: 0 }}>
                        <MapaCanvas
                            mode="overview" bistro={bistro} zastavky={localStops} mista={localMista}
                            kandidati={kandidati} onKandidat={pridat}
                            routeGeometry={fullRoute.geometry} routeDurationSec={fullRoute.totalDurationSec}
                            tileUrl={mapConfig?.tile_url} tileAttribution={mapConfig?.tile_attribution}
                            isDark={isDark} fitPadding={fitPadding}
                        />
                    </div>
                )}
                <div style={{ position: 'absolute', top: 'calc(12px + env(safe-area-inset-top, 0px))', left: 16, right: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', zIndex: 2 }}>
                    <Zpet onClick={onZpet} isDark={isDark} />
                    {davka?.cas_navratu_predpoklad && (
                        <div style={{ ...plocha({ zaobleni: 14 }), padding: '8px 12px', textAlign: 'right' }}>
                            <Nadpis style={{ fontSize: 11 }}>Zpět v bistru</Nadpis>
                            <div style={{ fontSize: 18, fontWeight: 900 }}>{hm(davka.cas_navratu_predpoklad)}</div>
                        </div>
                    )}
                </div>
                {(fullRoute.loading || busy === 'preradit' || String(busy).startsWith('pridat-')) && (
                    <div style={{ position: 'absolute', bottom: 36, left: 16, zIndex: 2 }}>
                        <Stitek><Loader2 size={11} className="animate-spin" /> skládám trasu</Stitek>
                    </div>
                )}
            </div>

            <section style={{ ...spodniList(), paddingBottom: 0, flex: 1, minHeight: 0, marginTop: -24, position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column' }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--border-strong)', alignSelf: 'center', margin: '10px 0 6px' }} />
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '0 16px 8px' }}>
                    <h1 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>{davka ? `Trasa ${davka.kod}` : 'Skládání trasy'}</h1>
                    {localStops.length > 0 && (
                        <span style={{ fontSize: 13, fontWeight: 800, color: pozde ? CHYBA : OK }}>
                            {pozde ? `${pozde}× po termínu` : localStops.length > 1 ? 'všechny včas' : 'včas'}
                        </span>
                    )}
                </div>

                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {diff && <OptimumBanner diff={diff} onVratit={handleVratitOptimum} busy={busy === 'preradit'} />}

                    {localMista.length > 0 && pozde > 0 && (
                        <div style={{ ...plocha({ zaobleni: 14 }), padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start', border: `1px solid ${tint(POZOR, 0.5)}`, fontSize: 13, fontWeight: 700 }}>
                            <AlertTriangle size={18} style={{ color: POZOR, flexShrink: 0 }} />
                            <span>
                                Po slibu: {localStops.filter(z => (z.rozdil_sec ?? 0) > 0).map(z => `#${z.cislo_denni ?? z.cislo_objednavky} (+${Math.round(z.rozdil_sec / 60)} min)`).join(', ')}.
                                <span style={{ color: 'var(--text-secondary)' }}> Zkus zastávku přetáhnout na cestu zpět.</span>
                            </span>
                        </div>
                    )}

                    {localStops.length > 0 ? (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={trasa.map(i => i.klic)} strategy={verticalListSortingStrategy}>
                                {trasa.map((i) => i.typ === 'm' ? (
                                    <SortableMisto key={i.klic} klic={i.klic} m={i.m}
                                        onPobyt={(min) => upravitMisto(i.m, min)}
                                        onOdebrat={() => odebratMisto(i.m)}
                                        pracuje={busy === `misto-${i.m.id}`}
                                    />
                                ) : (
                                    <SortableZastavka
                                        key={i.klic} z={i.z} idx={localStops.indexOf(i.z) + 1}
                                        onVratit={() => vratit(i.z.objednavka_id)}
                                        vraciSe={busy === `vratit-${i.z.objednavka_id}`}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <div style={{ ...plocha({ zaobleni: 16 }), padding: '14px 16px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                            Ťukni na objednávku v teple nebo na její bod v mapě — přidá se do trasy a pořadí se srovná samo.
                        </div>
                    )}

                    {localStops.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {!pridatDalsi && (
                                <button type="button" onClick={() => setPridatDalsi(true)} disabled={hotove.length === 0} style={{
                                    border: 'none', background: 'transparent', font: 'inherit', cursor: hotove.length ? 'pointer' : 'default',
                                    padding: '8px 6px', fontSize: 13.5, fontWeight: 700, color: 'var(--text-secondary)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                }}>
                                    <Plus size={15} />{hotove.length ? `přidat další z tepla (${hotove.length})` : 'v teple nic dalšího nečeká'}
                                </button>
                            )}
                            {/* Benzinka nebo obchod po cestě (2026-10-29) */}
                            <button type="button" onClick={() => setMistaOtevrena(true)} disabled={!davka} style={{
                                border: `1.5px solid ${tint(AKCENT, 0.5)}`, background: 'transparent', font: 'inherit', cursor: 'pointer',
                                height: 40, padding: '0 14px', borderRadius: 999, fontSize: 13.5, fontWeight: 800, color: AKCENT,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            }}>
                                <Fuel size={15} /> Zastávka
                            </button>
                        </div>
                    )}
                    {(localStops.length === 0 || pridatDalsi) && (<>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 6 }}>
                        <Nadpis>V teple{hotove.length ? ` · ${hotove.length}` : ''}</Nadpis>
                        {!naSmene && <span style={{ fontSize: 12.5, fontWeight: 700, color: POZOR }}>jen rozvozce na směně</span>}
                    </div>
                    {teplo.err && <div style={{ fontSize: 13, fontWeight: 700, color: CHYBA }}>V teple se nenačetlo: {teplo.err}</div>}
                    {teplo.data && hotove.length === 0 && (
                        <div style={{ padding: '8px 2px', fontSize: 14, color: 'var(--text-secondary)' }}>V teple teď nic nečeká.</div>
                    )}
                    {hotove.map(o => (
                        <KartaTepla key={o.id} o={o} pridava={busy === `pridat-${o.id}`}
                            disabled={!naSmene || !!busy} onPridat={() => pridat(o.id)} />
                    ))}

                    {variSe.length > 0 && (
                        <>
                            <Nadpis style={{ marginTop: 6 }}>Za chvíli hotové</Nadpis>
                            {variSe.map(o => (
                                <div key={o.id} style={{
                                    display: 'flex', alignItems: 'center', gap: 12, minHeight: 52,
                                    padding: '8px 14px', borderRadius: 14,
                                    border: '1.5px dashed var(--border-strong)', color: 'var(--text-secondary)', fontSize: 14,
                                }}>
                                    <ChefHat size={18} style={{ flexShrink: 0 }} />
                                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <b style={{ color: 'var(--text-primary)' }}>#{o.cislo}</b> · {o.adresa || 'bez adresy'}
                                    </span>
                                </div>
                            ))}
                        </>
                    )}
                    </>)}

                    {err && <div style={{ fontSize: 13, fontWeight: 700, color: CHYBA }}>{err}</div>}
                </div>

                <footer style={{ padding: '10px 16px calc(16px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid var(--border-default)', display: 'flex', gap: 10 }}>
                    {stav === 'pripraveno' && (
                        <Tlacitko onClick={() => post(`/api/rozvoz/davky/${davka.id}/prijmout`)} disabled={!!busy} style={{ padding: '0 16px' }}>
                            Přijmout
                        </Tlacitko>
                    )}
                    <Tlacitko plne barva={AKCENT} onClick={() => setKrok('kontrola')} disabled={!!busy || localStops.length === 0} style={{ flex: 1, minHeight: 64 }}>
                        {localStops.length === 0 ? 'Nejdřív přidej objednávky' : (
                            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <span>Zkontrolovat a naložit</span>
                                <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.75 }}>{souhrnKusu(localStops)}</span>
                            </span>
                        )}
                        {localStops.length > 0 && <ArrowRight size={20} strokeWidth={2.6} />}
                    </Tlacitko>
                </footer>
            </section>

            {mistaOtevrena && davka && (
                <MistaSheet davkaId={davka.id} isDark={isDark}
                    onClose={() => setMistaOtevrena(false)}
                    onPridano={async (json) => {
                        setMistaOtevrena(false);
                        if (json?.mista) setLocalMista(json.mista);
                        if (json?.zastavky?.length) setLocalStops(json.zastavky);
                        await (onTiche ?? onRefresh)();
                    }}
                />
            )}

            {krok === 'kontrola' && davka && (
                <KontrolaObjednavek
                    zastavky={localStops} isDark={isDark}
                    onZpet={() => setKrok(null)}
                    busy={busy === nalozitUrl} err={err}
                    onNalozeno={async (chybi) => { if (await post(nalozitUrl, { body: { kontrola: { chybi } } })) setKrok(null); }}
                />
            )}
        </div>
    );
}

/** Objednávka v teple — ťuk = přidat do trasy. */
function KartaTepla({ o, pridava, disabled, onPridat }) {
    return (
        <button type="button" onClick={onPridat} disabled={disabled} style={{
            ...plocha({ zaobleni: 16 }),
            display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
            minHeight: 72, padding: '10px 12px 10px 14px', cursor: disabled ? 'default' : 'pointer',
            border: `1.5px dashed ${tint(AKCENT, 0.55)}`, opacity: disabled && !pridava ? 0.6 : 1,
            color: 'var(--text-primary)', WebkitTapHighlightColor: 'transparent',
        }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                    <span style={{ fontSize: 20, fontWeight: 900 }}>#{o.cislo}</span>
                    <span style={{ fontSize: 14.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.adresa || 'bez adresy'}</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>{[leziMin(o.hotovo_v), o.cas_slibeny && `slíbeno ${String(o.cas_slibeny).slice(0, 5)}`].filter(Boolean).join(' · ')}</span>
                    {o.zaplaceno
                        ? <Stitek barva={OK}>zaplaceno</Stitek>
                        : <Stitek barva={AKCENT}>{ocekavanaPlatba(o) === 'kartou' ? 'kartou' : 'hotově'} {kc(o.castka_kc)}</Stitek>}
                </div>
            </div>
            <span style={{
                width: 44, height: 44, borderRadius: 22, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: tint(AKCENT, 0.16), color: AKCENT,
            }}>
                {pridava ? <Loader2 size={20} className="animate-spin" /> : <Plus size={22} strokeWidth={2.6} />}
            </span>
        </button>
    );
}

function leziMin(iso) {
    if (!iso) return 'hotová';
    const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    return min < 1 ? 'hotová teď' : `leží ${min} min`;
}

/**
 * U regálu: každá krabice (štítek 1/3, 2/3…), každý společný štítek
 * (Přílohy…, svůj štítek se soupisem) a každá položka bez štítku se
 * odťukne zvlášť — tu rozvozce vidí rovnou s názvem a variantou, protože
 * na ní žádný štítek není. Co a jak dostane štítek, se nastavuje u položek
 * v OS (2026-10-23). Rozvozce u regálu nic nehlásí (2026-10-26 — omylem
 * naklikané reklamace): co chybí, řekne na pokladně, nebo to nahlásí
 * zákazník sám na stránce objednávky.
 */
function kusyZastavky(z) {
    const s = z.soupis;
    const id = z.objednavka_id;
    if (!s) {
        const n = Math.max(1, z.krabic || 0);
        return Array.from({ length: n }, (_, i) => ({ klic: `${id}-k${i}`, druh: 'krabice', text: `${i + 1}/${n}`, nazev: 'Položka' }));
    }
    const kusy = Array.from({ length: s.krabic }, (_, i) => ({
        klic: `${id}-k${i}`, druh: 'krabice', text: `${i + 1}/${s.krabic}`, nazev: s.krabice?.[i] || 'Položka',
    }));
    // Společné štítky po skupinách; starší server posílá jen jednu tašku příloh.
    const skupiny = s.skupiny ?? (s.taska ? [{ nazev: 'Přílohy', obsah: s.taska.obsah }] : []);
    skupiny.forEach((g, i) => {
        kusy.push({ klic: `${id}-t${i}`, druh: 'taska', skupina: g.nazev, text: g.nazev, nazev: g.nazev, polozky: g.obsah, obsah: g.obsah.map(t => `${t.pocet}× ${t.nazev}`).join(' · ') });
    });
    // Bez štítku — rozvozce kontroluje přímo položku (název nese i variantu).
    (s.polozky ?? s.napoje.map(n => ({ ...n, napoj: true }))).forEach((n, i) => {
        for (let j = 0; j < n.pocet; j++) kusy.push({ klic: `${id}-n${i}-${j}`, druh: 'napoj', napoj: n.napoj, text: n.nazev, nazev: n.nazev });
    });
    if (kusy.length === 0) kusy.push({ klic: `${id}-k0`, druh: 'krabice', text: '1/1', nazev: 'Položka' });
    return kusy;
}

/**
 * „3 položky · 1 společný štítek · 2 kusy bez štítku" — co se u regálu bude brát.
 * „Položka", ne „krabice": se štítkem jde i sáček nebo kelímek a slovo
 * krabice rozvozce mátlo (2026-10-23).
 */
function souhrnKusu(zastavky) {
    const kusy = zastavky.flatMap(kusyZastavky);
    const n = (druh) => kusy.filter(k => k.druh === druh).length;
    const sklon = (c, jedna, dve, pet) => `${c} ${c === 1 ? jedna : c < 5 ? dve : pet}`;
    return [
        n('krabice') && sklon(n('krabice'), 'položka', 'položky', 'položek'),
        n('taska') && sklon(n('taska'), 'společný štítek', 'společné štítky', 'společných štítků'),
        n('napoj') && sklon(n('napoj'), 'kus bez štítku', 'kusy bez štítku', 'kusů bez štítku'),
    ].filter(Boolean).join(' · ');
}

/**
 * U regálu — po objednávkách, ne po kusech (Cockpit 2.0, 2026-10-19).
 *
 * Dřív se odťukával každý kus zvlášť (krabice 1/3, 2/3…, taška, každý
 * nápoj), u velké jízdy desítky ťuknutí. Teď má každá objednávka jedno
 * „Mám" a obsah je vidět jako štítky — jen ke kontrole, „chybí" se tu
 * od 2026-10-26 neťuká (reklamace hlásí zákazník).
 *
 * „Naloženo" jen uzavře kontrolu — jízda začne až Vyjíždím v autě.
 *
 * Pořadí je **od poslední zastávky** (2026-10-23): taška se skládá odspodu,
 * takže první se nakládá to, co se vydá naposled, a navrch jde první
 * zastávka.
 */
function KontrolaObjednavek({ zastavky, busy, err, isDark, onZpet, onNalozeno }) {
    const [uctenka, setUctenka] = useState(null);   // zastávka, jejíž účtenku rozvozce kontroluje
    const poZastavkach = zastavky
        .map((z, i) => ({ z, zastavka: i + 1, kusy: kusyZastavky(z).map(k => ({ ...k, z })) }))
        .reverse();
    // „Mám" se ukládá na server (2026-10-23) — kuchyň ho vidí na displeji PVD
    // a po zavření aplikace se načte zpátky ze zastávky (`nalozeno`).
    const [mam, setMam] = useState(() => new Set(zastavky.filter(z => z.nalozeno).map(z => z.objednavka_id)));
    const odtuknoutMam = (id) => {
        const nove = !mam.has(id);
        prepni(setMam, id);
        fetch(`/api/rozvoz/zastavky/${id}/mam`, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ mam: nove }),
        }).catch(() => { /* bez sítě zůstane jen v telefonu, odjezd to stejně uloží */ });
    };
    const prepni = (set, klic) => set(s => { const n = new Set(s); if (n.has(klic)) n.delete(klic); else n.add(klic); return n; });

    const hotovo = poZastavkach.filter(({ z }) => mam.has(z.objednavka_id)).length;
    const vse = hotovo === poZastavkach.length;

    return (
        <div style={{ position: 'absolute', inset: 0, zIndex: 20, background: 'var(--surface-base)', color: 'var(--text-primary)', display: 'flex', flexDirection: 'column' }}>
            <header style={{ padding: 'calc(16px + env(safe-area-inset-top, 0px)) 16px 10px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <Zpet onClick={onZpet} popis="Zpět na trasu" />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Nadpis style={{ lineHeight: 1.2 }}>U regálu</Nadpis>
                    <h1 style={{ margin: 0, fontFamily: 'var(--pismo-display)', fontWeight: 400, fontSize: 28, lineHeight: 1.1, textTransform: 'uppercase' }}>Máš všechno?</h1>
                </div>
                <span style={{ fontSize: 15, fontWeight: 800, color: vse ? OK : 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{hotovo} z {poZastavkach.length}</span>
            </header>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 16px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', padding: '0 4px' }}>
                    Od poslední zastávky — skládej odspodu, navrch půjde 1. zastávka.
                </div>
                {poZastavkach.map(({ z, zastavka, kusy }) => {
                    const ok = mam.has(z.objednavka_id);
                    return (
                        <section key={z.objednavka_id} className="papir listek" style={{
                            borderRadius: 14, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 9,
                            outline: ok ? 'none' : `2.5px solid ${tint(AKCENT, 0.9)}`, outlineOffset: -1,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                <span title={`${zastavka}. zastávka`} style={{
                                    minWidth: 28, height: 28, borderRadius: 14, flexShrink: 0, padding: '0 6px',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'rgba(22,22,26,0.85)', color: '#fff', fontSize: 14, fontWeight: 900,
                                }}>{zastavka}.</span>
                                <span style={{ fontFamily: 'var(--pismo-display)', fontSize: 24, lineHeight: 1, flexShrink: 0 }}>#{z.cislo_denni ?? z.cislo_objednavky}</span>
                                <span style={{ flex: 1, minWidth: 0, fontSize: 15.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{z.adresa || '—'}</span>
                            </div>
                            {/* Na vlastním řádku — se třemi tlačítky by adrese nezbylo místo. */}
                            <div style={{ display: 'flex', gap: 8 }}>
                                {/* Účtenka — porovnat, co je v tašce, s tím, co si zákazník objednal */}
                                <button type="button" onClick={() => setUctenka(z)} aria-label={`Účtenka #${z.cislo_denni ?? z.cislo_objednavky}`} title="Účtenka" style={{
                                    flex: 1, minWidth: 0, minHeight: 52, borderRadius: 13,
                                    border: '2px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)',
                                    font: 'inherit', fontSize: 14, fontWeight: 800,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer',
                                    WebkitTapHighlightColor: 'transparent',
                                }}><Receipt size={18} />Účtenka</button>
                                <StitekTasky objednavkaId={z.objednavka_id} cislo={z.cislo_denni ?? z.cislo_objednavky} />
                                <button type="button" onClick={() => odtuknoutMam(z.objednavka_id)} aria-pressed={ok} style={{
                                    flex: 1, minHeight: 52, minWidth: 0, borderRadius: 13,
                                    border: ok ? '2px solid var(--papir-ok)' : '2px solid var(--border-strong)',
                                    background: ok ? 'var(--papir-ok)' : 'transparent', color: ok ? '#fff' : 'var(--text-primary)',
                                    font: 'inherit', fontSize: 15, fontWeight: 800, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                    WebkitTapHighlightColor: 'transparent',
                                }}>{ok && <Check size={18} strokeWidth={3.2} />}Mám</button>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {kusy.map(k => {
                                    const Ikona = k.druh === 'taska' ? ShoppingBag : k.druh === 'napoj' ? (k.napoj ? CupSoda : Tag) : Package;
                                    return (
                                        <span key={k.klic} style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 34, padding: '0 10px',
                                            borderRadius: 9, background: 'rgba(22,22,26,0.06)', color: 'var(--text-primary)',
                                            fontSize: 13, fontWeight: 700, maxWidth: '100%',
                                        }}>
                                            <Ikona size={14} style={{ flexShrink: 0 }} />
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {k.druh === 'krabice' ? k.text : k.druh === 'taska' ? `${k.skupina || 'taška'} · ${k.obsah}` : k.text}
                                            </span>
                                        </span>
                                    );
                                })}
                            </div>
                            {z.poznamka && (
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
                                    Poznámka: <b style={{ color: 'var(--text-primary)' }}>{z.poznamka}</b>
                                </div>
                            )}
                        </section>
                    );
                })}
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'center', padding: '2px 8px' }}>
                    Něco chybí? Řekni to hned na pokladně, než vyjedeš.
                </div>
            </div>

            <footer style={{ padding: '10px 16px calc(16px + env(safe-area-inset-bottom, 0px))' }}>
                {err && <div style={{ fontSize: 13, fontWeight: 700, color: CHYBA, marginBottom: 8 }}>{err}</div>}
                <Tlacitko plne barva={OK} onClick={() => onNalozeno([])} disabled={!vse || busy} style={{ width: '100%', minHeight: 64 }}>
                    {busy && <Loader2 size={20} className="animate-spin" />}
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span>Naloženo</span>
                        <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.75 }}>
                            {!vse ? `zbývá potvrdit ${poZastavkach.length - hotovo}` : 'všechno máš — jdi k autu'}
                        </span>
                    </span>
                    {vse && !busy && <ArrowRight size={20} strokeWidth={2.6} />}
                </Tlacitko>
            </footer>
            {uctenka && <UctenkaModal zastavka={uctenka} onClose={() => setUctenka(null)} isDark={isDark} />}
        </div>
    );
}

/**
 * Štítek na tašku (2026-11-03) — vytiskne pokladna se zapnutým tiskem štítků.
 * Každé ťuknutí = jeden štítek, na další tašku stačí ťuknout znovu. Pokladna
 * hlásí výsledek, aplikace se na něj ptá; rozvozce stojí u regálu a musí vědět,
 * jestli má jít ke štítku, nebo tisk zkusit znovu.
 */
function StitekTasky({ objednavkaId, cislo }) {
    const [stav, setStav] = useState(null);   // null | 'tiskne' | 'ok' | 'chyba'
    const [zprava, setZprava] = useState(null);
    const [vytisteno, setVytisteno] = useState(0);
    const zivy = useRef(true);
    useEffect(() => { zivy.current = true; return () => { zivy.current = false; }; }, []);

    const vyhodnotit = (d) => {
        if (d.stav === 'vytisteno') {
            setStav('ok');
            setVytisteno(n => n + 1);
            return true;
        }
        if (d.stav === 'chyba' || d.stav === 'propadlo') {
            setStav('chyba');
            setZprava(d.stav === 'chyba' ? (d.chyba || 'Štítek se nevytiskl.') : 'Žádná pokladna netiskne štítky. Zkontroluj tiskárnu na pobočce.');
            return true;
        }
        return false;
    };

    const tisknout = async () => {
        if (stav === 'tiskne') return;
        setStav('tiskne');
        setZprava(null);
        try {
            const r = await fetch(`/api/rozvoz/zastavky/${objednavkaId}/stitek-tasky`, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
            if (vyhodnotit(d)) return;

            // Server požadavek po 75 s sám ukončí; výpadek sítě se přečká.
            const konec = Date.now() + 100000;
            while (zivy.current && Date.now() < konec) {
                await new Promise(t => setTimeout(t, 1500));
                const dotaz = await fetch(`/api/rozvoz/stitky-tasek/${d.id}`, { credentials: 'same-origin' }).catch(() => null);
                const novy = dotaz?.ok ? await dotaz.json().catch(() => null) : null;
                if (novy && vyhodnotit(novy)) return;
            }
            if (zivy.current) {
                setStav('chyba');
                setZprava('Nepodařilo se ověřit, jestli štítek vyjel. Podívej se k tiskárně.');
            }
        } catch (e) {
            if (!zivy.current) return;
            setStav('chyba');
            setZprava(e?.message || 'Požadavek se nepodařilo odeslat.');
        }
    };

    const hotovo = stav === 'ok';
    const barva = stav === 'chyba' ? CHYBA : hotovo ? 'var(--papir-ok)' : 'var(--border-strong)';
    return (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button type="button" onClick={tisknout} disabled={stav === 'tiskne'} aria-label={`Štítek na tašku #${cislo}`} style={{
                minHeight: 52, borderRadius: 13, border: `2px solid ${barva}`, background: 'transparent',
                color: stav === 'chyba' ? CHYBA : hotovo ? 'var(--papir-ok)' : 'var(--text-primary)',
                font: 'inherit', fontSize: 14, fontWeight: 800, cursor: stav === 'tiskne' ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                WebkitTapHighlightColor: 'transparent',
            }}>
                {stav === 'tiskne' ? <Loader2 size={18} className="animate-spin" /> : hotovo ? <Check size={18} strokeWidth={3.2} /> : <Printer size={18} />}
                {stav === 'tiskne' ? 'Tiskne se' : stav === 'chyba' ? 'Znovu' : vytisteno > 1 ? `Štítek ${vytisteno}×` : 'Štítek'}
            </button>
            {zprava && <div style={{ fontSize: 12.5, fontWeight: 700, color: CHYBA, lineHeight: 1.25 }}>{zprava}</div>}
        </div>
    );
}

function SortableZastavka({ z, idx, onVratit, vraciSe }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: z.objednavka_id });

    const rozdilMin = z.rozdil_sec != null ? Math.round(z.rozdil_sec / 60) : null;
    const barva = rozdilMin == null ? 'var(--text-muted)' : rozdilMin > 5 ? CHYBA : rozdilMin > 0 ? POZOR : OK;
    const platba = stitekPlatby(z);

    return (
        <div ref={setNodeRef} className="papir listek" style={{
            ...plocha({ zaobleni: 16 }),
            transform: CSS.Transform.toString(transform), transition,
            zIndex: isDragging ? 10 : 1, opacity: isDragging ? 0.85 : 1,
            border: `1px solid ${isDragging ? AKCENT : 'var(--border-default)'}`,
            display: 'flex', alignItems: 'center', gap: 10, minHeight: 72, padding: '8px 10px 8px 4px',
            touchAction: 'pan-y',
        }}>
            <button {...attributes} {...listeners} aria-label="Přetáhnout pořadí" style={{
                width: 32, height: 56, border: 'none', background: 'transparent', color: 'var(--text-muted)',
                cursor: 'grab', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', touchAction: 'none', flexShrink: 0,
            }}>
                <GripVertical size={18} />
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 16, background: tint(barva.startsWith('#') ? barva : '#8B8B9A', 0.9), color: '#0A0A0C', fontWeight: 900, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{idx}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    #{z.cislo_denni ?? z.cislo_objednavky} · {z.adresa || '—'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                    příjezd {hm(z.cas_predpoklad)}{z.cas_slibeny ? ` · slíbeno ${String(z.cas_slibeny).slice(0, 5)}` : ''}
                </div>
                <div style={{ marginTop: 5 }}><Stitek barva={platba.barva}>{platba.text}</Stitek></div>
            </div>
            <button type="button" onClick={onVratit} disabled={vraciSe} aria-label="Vrátit do tepla" title="Vrátit do tepla" style={{
                width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                border: '1px solid var(--border-default)', background: 'var(--surface-elevated)',
                color: 'var(--text-secondary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {vraciSe ? <Loader2 size={16} className="animate-spin" /> : <Undo2 size={16} />}
            </button>
        </div>
    );
}

/** Benzinka / obchod v trase — čárkovaná karta, přetahuje se jako objednávka. */
function SortableMisto({ klic, m, onPobyt, onOdebrat, pracuje }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: klic });
    const Ik = m.druh === 'benzinka' ? Fuel : ShoppingCart;
    const btn = {
        width: 36, height: 36, borderRadius: 10, flexShrink: 0, cursor: 'pointer',
        border: '1px solid var(--border-default)', background: 'var(--surface-elevated)', color: 'var(--text-secondary)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    };
    return (
        <div ref={setNodeRef} style={{
            transform: CSS.Transform.toString(transform), transition,
            zIndex: isDragging ? 10 : 1, opacity: isDragging ? 0.85 : 1,
            borderRadius: 16, border: `1.5px dashed ${isDragging ? AKCENT : 'var(--border-strong)'}`,
            display: 'flex', alignItems: 'center', gap: 8, minHeight: 64, padding: '6px 8px 6px 4px',
            touchAction: 'pan-y',
        }}>
            <button {...attributes} {...listeners} aria-label="Přetáhnout pořadí" style={{
                width: 32, height: 52, border: 'none', background: 'transparent', color: 'var(--text-muted)',
                cursor: 'grab', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', touchAction: 'none', flexShrink: 0,
            }}>
                <GripVertical size={18} />
            </button>
            <span style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: tint(AKCENT, 0.14), color: AKCENT }}>
                <Ik size={17} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.nazev}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 1 }}>
                    {cinnostMista(m.druh)} {m.pobyt_min} min{m.cas_predpoklad ? ` · příjezd ${hm(m.cas_predpoklad)}` : ''}
                </div>
            </div>
            <button type="button" onClick={() => onPobyt(Math.max(1, m.pobyt_min - 1))} aria-label="Méně minut" style={btn}><Minus size={15} /></button>
            <button type="button" onClick={() => onPobyt(Math.min(60, m.pobyt_min + 1))} aria-label="Víc minut" style={btn}><Plus size={15} /></button>
            <button type="button" onClick={onOdebrat} disabled={pracuje} aria-label="Odebrat zastávku" title="Odebrat zastávku" style={btn}>
                {pracuje ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />}
            </button>
        </div>
    );
}

function OptimumBanner({ diff, onVratit, busy }) {
    const skluzy = diff.aktualni?.pocet_skluzu ?? 0;
    const deltaSec = diff.delta_navrat_sec ?? 0;
    const deltaMin = Math.round(deltaSec / 60);
    const optimum = skluzy === (diff.optimum?.pocet_skluzu ?? 0) && Math.abs(deltaSec) < 30;
    const barva = optimum ? OK : skluzy > 0 ? CHYBA : AKCENT;
    const Icon = optimum ? Sparkles : AlertTriangle;

    return (
        <div style={{ ...plocha({ zaobleni: 14 }), padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, border: `1px solid ${tint(barva, 0.5)}` }}>
            <Icon size={18} style={{ color: barva, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700 }}>
                {optimum
                    ? <span style={{ color: barva }}>Tvoje pořadí je nejrychlejší</span>
                    : <><span style={{ color: barva }}>{skluzy > 0 ? `${skluzy}× po termínu` : 'V termínu'}</span>
                        <span style={{ color: 'var(--text-secondary)' }}> · návrat {deltaMin > 0 ? `+${deltaMin}` : deltaMin} min proti nejrychlejšímu</span></>}
            </div>
            {!optimum && (
                <Tlacitko barva={barva} vyska={44} Icon={ArrowUpDown} onClick={onVratit} disabled={busy} style={{ fontSize: 13, padding: '0 10px' }}>
                    Nejrychlejší
                </Tlacitko>
            )}
        </div>
    );
}
